/**
 * StatTile — la petite tuile : une étiquette, un chiffre. Deux usages dans les
 * captures : la grille « Abonnés actifs 912 · ARR 87,9 k$US » et la rangée par
 * jour « 23 oct. — 219,88 $US », où la tuile du jour est `active`
 * (surface plus claire, hairline néon, halo doux).
 *
 * Même mécanique que `StatCard` (compteur, formats fr-FR, `tone`, `delta`,
 * `sous`), sans sparkline. Avec `onClick`, un vrai bouton.
 *
 * Le RELIEF de la `StatCard` (Kayla, 27-10-2026, devant la bande des chiffres
 * de RishFans : « encore tout ça », une étiquette et un chiffre à plat) : le
 * même verre, la même grille de points qui s'évanouit, la pastille de
 * variation contre le chiffre. En `md`, le chiffre suit la largeur de la
 * tuile (`cqw`) : 30 px sur une tuile large, 20 px au plus serré ; sur une
 * tuile étroite, l'icône s'efface et l'étiquette se resserre plutôt que de
 * se couper (cf `stat.css`).
 */
import type { ReactNode } from "react";

import { cn } from "../utils";

import { Delta, type ValeurDelta } from "./Delta";
import { decimalesDe, formatValeur, type FormatValeur } from "./format";
import type { ToneStat } from "./StatCard";
import { useCompteur } from "./useCompteur";

export interface StatTileProps {
  label: string;
  icon?: ReactNode;
  /** Un nombre (formaté ici, compteur qui monte) — ou une chaîne DÉJÀ
   *  formatée par l'écran (« $2,574 », « — » quand c'est inconnu), telle quelle. */
  value: number | string;
  format?: FormatValeur;
  devise?: string;
  /** Un montant : `"auto"` (défaut) se compacte dès 10 000 (« 87,9 k$US ») ; `false` s'écrit entier
   *  (« 12 345,67 $US ») ; `true` toujours compact. */
  compact?: boolean | "auto";
  /** Force les décimales (sinon 0 si entier, 2 pour un montant à centimes, 1 pour un nombre). */
  decimales?: number;
  /** La tuile « du moment » (Aujourd'hui) : surlignée. */
  active?: boolean;
  /** Variation signée en % vs la période d'avant, ou `"nouveau"` (rien avant). */
  delta?: ValeurDelta;
  deltaInverse?: boolean;
  /** Le mot de `delta="nouveau"`, traduit par l'écran (défaut « Nouveau »). */
  libelleNouveau?: string;
  /** Un petit complément après le chiffre (« 53 % » sous « Encore là »). */
  sous?: ReactNode;
  tone?: ToneStat;
  /** `"sm"` (rangée par jour) ou `"md"` (grille de chiffres clés, défaut). */
  taille?: "sm" | "md";
  onClick?: () => void;
  title?: string;
  className?: string;
}

export function StatTile({
  label,
  icon,
  value,
  format = "montant",
  devise = "EUR",
  compact,
  decimales: decimalesDonnees,
  active = false,
  delta,
  deltaInverse,
  libelleNouveau,
  sous,
  tone = "marque",
  taille = "md",
  onClick,
  title,
  className,
}: StatTileProps) {
  const nombre = typeof value === "number" ? value : null;
  const affiche = useCompteur(nombre ?? 0);
  const decimales = decimalesDonnees ?? decimalesDe(nombre ?? 0, format === "montant" ? 2 : 1);
  const texte: string = nombre === null ? String(value) : formatValeur(affiche, format, devise, decimales, compact);
  const Racine = onClick ? "button" : "div";

  return (
    <Racine
      type={onClick ? "button" : undefined}
      onClick={onClick}
      title={title}
      data-tone={tone}
      data-active={active ? "true" : undefined}
      className={cn(
        "ui-stat ui-stat-carte ui-stat-tuile ui-stat-arrondi-sm relative flex w-full flex-col justify-between overflow-hidden text-left",
        taille === "md" ? "min-h-[92px] gap-3 px-4 py-3.5" : "min-h-[72px] gap-1.5 px-3.5 py-3",
        onClick && "cursor-pointer focus-visible:outline-none",
        className,
      )}
    >
      <div className="ui-stat-points ui-stat-points-tuile" />
      <div className="ui-stat-etiquette relative z-[1] flex min-w-0 items-center gap-1.5">
        {icon ? <span className="ui-stat-icone shrink-0">{icon}</span> : null}
        <span className="truncate">{label}</span>
      </div>
      {/* `flex-wrap` : un montant long (« 12 345,67 $US ») sur une tuile étroite pousse la pastille à la
          ligne au lieu de la rogner (vague 2). */}
      <div className="relative z-[1] flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          className={cn("ui-stat-valeur", taille === "md" ? "ui-stat-moyenne" : "text-[19px]")}
          aria-label={texte}
        >
          {texte}
        </span>
        {delta !== undefined ? <Delta valeur={delta} inverse={deltaInverse} libelleNouveau={libelleNouveau} /> : null}
        {sous ? <span className="ui-stat-sous truncate">{sous}</span> : null}
      </div>
    </Racine>
  );
}
